import React, { useRef, useState, useCallback, useEffect } from 'react';

/**
 * BeforeAfterCompare
 * Interactive drag/click before-and-after slider. Fully accessible:
 *  - Keyboard: ArrowLeft/ArrowRight adjust position, Home/End jump
 *  - Screen readers: aria-valuenow reports current percentage
 *
 * Props:
 *  - before: { src, alt } (image shown on the LEFT / underneath)
 *  - after: { src, alt } (image shown on the RIGHT / on top, revealed by slider)
 *  - initial: number 0-100 (default 50)
 *  - aspectClass: Tailwind aspect class (default 'aspect-[3/4] sm:aspect-[4/5]')
 */
export function BeforeAfterCompare({
  before,
  after,
  initial = 50,
  aspectClass = 'aspect-[3/4] sm:aspect-[4/5]',
  testId = 'before-after',
}) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(initial);
  const [dragging, setDragging] = useState(false);

  const clamp = (n) => Math.max(0, Math.min(100, n));

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(clamp(pct));
  }, []);

  const onPointerDown = (e) => {
    setDragging(true);
    setFromClientX(e.clientX);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragging) return;
    setFromClientX(e.clientX);
  };
  const onPointerUp = (e) => {
    setDragging(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };
  const onClick = (e) => setFromClientX(e.clientX);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); setPos((p) => clamp(p - 4)); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); setPos((p) => clamp(p + 4)); }
    else if (e.key === 'Home') { e.preventDefault(); setPos(0); }
    else if (e.key === 'End') { e.preventDefault(); setPos(100); }
  };

  // Prevent text selection while dragging
  useEffect(() => {
    if (!dragging) return undefined;
    document.body.style.userSelect = 'none';
    return () => { document.body.style.userSelect = ''; };
  }, [dragging]);

  return (
    <div
      ref={containerRef}
      className={`surface relative overflow-hidden select-none ${aspectClass} cursor-ew-resize`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClick={onClick}
      data-testid={testId}
    >
      {/* BEFORE image (base) */}
      <img
        src={before.src}
        alt={before.alt}
        draggable={false}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        data-testid={`${testId}-before-img`}
      />
      <span
        className="absolute top-3 left-3 z-10 px-3 py-1 text-xs font-bold uppercase tracking-wide bg-[rgba(9,11,14,0.75)] backdrop-blur-sm text-white border border-[color:var(--ft-border)] rounded-full"
        aria-hidden="true"
      >
        Before
      </span>

      {/* AFTER image clipped to slider position */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
        aria-hidden="true"
      >
        <img
          src={after.src}
          alt={after.alt}
          draggable={false}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid={`${testId}-after-img`}
        />
      </div>
      <span
        className="absolute top-3 right-3 z-10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-black rounded-full"
        style={{ background: 'var(--ft-accent)' }}
        aria-hidden="true"
      >
        After
      </span>

      {/* Divider + handle */}
      <div
        className="absolute top-0 bottom-0 pointer-events-none"
        style={{ left: `${pos}%`, transform: 'translateX(-1px)' }}
        aria-hidden="true"
      >
        <div className="w-0.5 h-full bg-white/90 shadow-[0_0_12px_rgba(0,0,0,0.5)]" />
      </div>

      <button
        type="button"
        role="slider"
        aria-label="Drag to compare before and after"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        aria-valuetext={`After image ${Math.round(100 - pos)}% revealed`}
        onKeyDown={onKeyDown}
        className="absolute top-1/2 h-11 w-11 -translate-y-1/2 rounded-full border-2 border-white bg-[rgba(9,11,14,0.8)] backdrop-blur-sm text-white flex items-center justify-center shadow-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-accent"
        style={{ left: `${pos}%`, transform: 'translate(-50%, -50%)' }}
        data-testid={`${testId}-handle`}
      >
        {/* Chevron cluster */}
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="15 18 9 12 15 6" />
          <polyline points="9 18 15 12 9 6" transform="translate(6 0)" />
        </svg>
      </button>

      {/* Bottom hint */}
      <div
        className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 px-3 py-1 text-[11px] font-medium bg-[rgba(9,11,14,0.65)] backdrop-blur-sm text-white border border-[color:var(--ft-border)] rounded-full"
        aria-hidden="true"
      >
        Drag to compare
      </div>
    </div>
  );
}

export default BeforeAfterCompare;
